import { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";

import { useLazyCheckAssessmentAccessQuery } from "@assessment/store/slices/api/assessmentApiSlice";
import { useAppDispatch } from "@assessment/store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { AccessStatus } from "@assessment/types/accessStatus";

import GetHeroIcon from "@assessment/utils/getHeroIcons";
import { useShowErrors } from "@assessment/pages/assessment-candidate/_components/toasterWrapper";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { AppMetaDataSliceActions } from "@lxp-app/store/slices/appMetaDataSlice";
import { useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";
import { DarkmodeAction } from "@lxp-app/store/slices/darkmodeSlice";
import NotScheduled from "@assessment/pages/assessment-candidate/_components/pre-assessment/access-variant-screens/notScheduled";
import { useTranslation } from "react-i18next";

const AssessmentAccessControlGuard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isDirectedFromViewer =
    location.search && location.search.includes("viewer");

  const isFeedback = location.pathname.includes("feedback");

  const showErrors = useShowErrors();

  const { scheduleId } = useParams();

  const mode = useSelector((state: RootState) => state.DarkmodeSlice);

  // remove breadcrumbs and disable dark mode.
  useLayoutEffect(() => {
    if (AppMetaDataSliceActions) {
      dispatch(AppMetaDataSliceActions.setViewBreadcrumb(false));
    }

    return () => {
      if (AppMetaDataSliceActions) {
        dispatch(AppMetaDataSliceActions.setViewBreadcrumb(true));
      }
    };
  }, [window.location.pathname]);

  const [accessAllowedForAssessment, setAccessAllowedForAssessment] =
    useState(false);
  const [accessStatusNotHandled, setAccessStatusNotHandled] = useState(false);
  const [callingApi, setCallingApi] = useState(false);
  const [notScheduled, setNotScheduled] = useState(false);
  const handledAccessStatus = [
    AccessStatus.SCHEDULE_NOT_STARTED,
    AccessStatus.SCHEDULE_TIME_COMPLETED,
    AccessStatus.LOGIN_WINDOW_COMPLETED,
    AccessStatus.MAX_ATTEMPT_EXCEEDED,
    AccessStatus.IN_PROGRESS,
    AccessStatus.PROGRESS_NOT_STARTED,
    AccessStatus.ATTEMPT_IN_RESULT_PROCESSING,
    AccessStatus.SCHEDULE_HAS_BEEN_CANCELLED,
    AccessStatus.COOL_OFF_PERIOD_NOT_FINISHED
  ];

  const [
    checkAssessmentAccess,
    {
      isFetching: isFetchingAccessData,
      isLoading: isLoadingAccessData,
      isError: isErrorAccessCheck,
      isSuccess: isSuccessAccessCheck
    }
  ] = useLazyCheckAssessmentAccessQuery();

  useEffect(() => {
    (async () => {
      try {
        setCallingApi(true);

        if (!scheduleId) {
          throw {
            status: "",
            title: "Schedule Id Not Found",
            err: {},
            description: ""
          };
        }

        const res = await checkAssessmentAccess({ scheduleId: scheduleId });

        // if (
        //   res.data.data &&
        //   res.data.data.accessStatus === AccessStatus.PROGRESS_NOT_STARTED &&
        //   !isDirectedFromViewer &&
        //   !isFeedback &&
        //   isErrorAccessCheck
        // ) {
        //   navigate(`/content-viewer/assessment/${scheduleId}`);
        //   return;
        // }
        if (
          res.error &&
          typeof res.error === "object" &&
          "status" in res.error
        ) {
          const status = (res.error as FetchBaseQueryError).status;

          if (status === 401) {
            setNotScheduled(true);
            dispatch(AssessmentSliceActions.mutateOnGoingId(undefined));
            dispatch(
              AssessmentSliceActions.mutateAccessDetails({
                accessAllowed: false,
                accessStatus: AccessStatus.NOT_SCHEDULED,
                ongoingQpDuration: 0
              })
            );
            navigate(`/content-viewer/assessment/${scheduleId}`, {
              replace: true
            });
            return;
          }
        }

        if (res.error) {
          throw {
            status: "",
            title: "Failed to check access for the assessment",
            err: res.error,
            description: ""
          };
        }

        // if (
        //   res.data.data &&
        //   res.data.data.accessStatus === AccessStatus.PROGRESS_NOT_STARTED &&
        //   !isDirectedFromViewer &&
        //   !isFeedback
        // ) {
        //   navigate(`/content-viewer/assessment/${scheduleId}`, {
        //     replace: true
        //   });
        //   return;
        // }

        const {
          accessAllowed,
          accessStatus,
          ongoingQpDuration,
          ongoingId,
          sessionId
        } = res?.data?.data;

        if (sessionId) {
          dispatch(AssessmentSliceActions.mutateVisitorId(sessionId));
        }

        dispatch(AssessmentSliceActions.mutateOnGoingId(ongoingId));
        dispatch(
          AssessmentSliceActions.mutateAccessDetails({
            ...res?.data?.data
          })
        );
        if (ongoingQpDuration) {
          dispatch(AssessmentSliceActions.mutateTimeLeft(ongoingQpDuration));
        }
        if (!handledAccessStatus.includes(accessStatus))
          setAccessStatusNotHandled(true);
        setAccessAllowedForAssessment(accessAllowed);
        setCallingApi(false);
      } catch (err) {
        dispatch(AssessmentSliceActions.mutateOnGoingId(undefined));
        console.error(err);
        const temp: any = err;
        const rawErrList = temp?.err?.data?.error;
        const errList =
          rawErrList?.length > 0
            ? rawErrList?.map((el: string) => el.split("_")[0])
            : [];
        if (showErrors && !rawErrList?.includes("107-106-10601001_error"))
          showErrors(temp?.title, errList, "error", temp?.description);
        setCallingApi(false);
      }
    })();
  }, [scheduleId, window.location.pathname]);

  if (isFetchingAccessData || isLoadingAccessData || callingApi) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <div className="w-8 h-8 border-t rounded-full border-t-primary border-secondary animate-spin" />
        <span>{t("common.loading")}</span>
      </div>
    );
  }

  if (isErrorAccessCheck && !notScheduled) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <GetHeroIcon
          name="ExclamationTriangleIcon"
          className="w-6 h-6"
          variant="outline"
        />
        <span>
          {t("assessment.fetchError", {
            featureName: t("assessment.featureNames.assessment")
          })}
        </span>
      </div>
    );
  }

  // if user is not scheduled for the assessment show error
  if (notScheduled && isErrorAccessCheck) {
    return <NotScheduled />;
  }

  if (isSuccessAccessCheck) {
    if (!accessAllowedForAssessment && accessStatusNotHandled)
      navigate("/error/forbidden", { replace: true });
    else return <Outlet />;
  }
};

export default AssessmentAccessControlGuard;
